import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useUsers, { useQueryEnum } from "../hooks/use-users";
import UserTable from "./user-table";
import { UserDetailsDrawer } from "./user-details-drawer";
import { DuplicateAccountsSheet } from "./duplicate-accounts-sheet";
import { SearchBar } from "@/components/common/search-bar";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import UserFilters from "./user-filter";
import { XIcon, InfoIcon } from "lucide-react";
import { ContentLayout } from "@/components/common/content-layout";
import { ConsentFormStatus } from "@/pages/users/types/user";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDuplicateIdentifiers } from "../hooks/use-duplicate-identifiers";
import { useDuplicateSearchSetting } from "../hooks/use-duplicate-search-setting";
import { cn } from "@/lib/utils";

const CONSENT_STATUSES = [
  "NOT_INITIATED",
  "PENDING",
  "APPROVED",
  "REJECTED",
] as const;

const 
UserList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDuplicatesSheetOpen, setIsDuplicatesSheetOpen] = useState(false);

  const page = Number(searchParams.get("page") || 0);
  const search = searchParams.get("search") || "";

  const is_lead_param = searchParams.get("is_lead");
  const product_id_param = searchParams.get("product_id");

  const hrc_status = useQueryEnum<ConsentFormStatus>(
    searchParams,
    "hrc_status",
    CONSENT_STATUSES
  );

  const mc_status = useQueryEnum<ConsentFormStatus>(
    searchParams,
    "mc_status",
    CONSENT_STATUSES
  );

  const { enabled: duplicateSearchEnabled, setEnabled: setDuplicateSearchEnabled } =
    useDuplicateSearchSetting();

  const is_lead_resolved =
    is_lead_param === "true" ? true : is_lead_param === "false" ? false : undefined;
  const product_id_resolved =
    product_id_param && product_id_param !== "ALL" ? product_id_param : undefined;

  const { data: duplicateData, isLoading: isDuplicatesLoading } =
    useDuplicateIdentifiers({
      search,
      is_lead: is_lead_resolved,
      product_id: product_id_resolved,
      hrc_status,
      mc_status,
      enabled: duplicateSearchEnabled,
    });

  const duplicateGroups = duplicateData?.groups ?? [];
  const hasDuplicates = duplicateGroups.length > 0;

  const { data, isLoading, isFetching, error } = useUsers({
    page,
    search,
    is_lead: is_lead_resolved,
    product_id: product_id_resolved,
    hrc_status,
    mc_status,
  });

  const users = data?.users ?? [];

  return (
    <ContentLayout
      header={
        <>
          {/* Row 1 — Filters + Search */}
          <div className="flex flex-wrap items-center gap-4">
            <UserFilters />
            <div className="flex-1 min-w-[200px]">
              <SearchBar
                placeholder="Search Users.."
                urlParamStrategy="prefix"
                clearButton={{
                  enabled: true,
                  position: "inside",
                  ariaLabel: "Clear search",
                  icon: <XIcon className="w-4 h-4" />,
                }}
              />
            </div>
          </div>

          {/* Row 2 — Duplicate check bar */}
          <div className="flex items-center justify-end">
            <TooltipProvider delayDuration={200}>
              <div className="flex items-center gap-2">
                {/* Toggle */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="flex items-center gap-1.5 cursor-pointer"
                      onClick={() => setDuplicateSearchEnabled(!duplicateSearchEnabled)}
                    >
                      <Checkbox
                        id="dup-search-toggle"
                        checked={duplicateSearchEnabled}
                        onCheckedChange={(checked) =>
                          setDuplicateSearchEnabled(Boolean(checked))
                        }
                      />
                      <label
                        htmlFor="dup-search-toggle"
                        className="text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap"
                      >
                        Check Duplicate Accounts
                      </label>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {duplicateSearchEnabled
                      ? "Disable duplicate account detection"
                      : "Enable duplicate account detection"}
                  </TooltipContent>
                </Tooltip>

                {/* Info icon */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => hasDuplicates && setIsDuplicatesSheetOpen(true)}
                      className={cn(
                        "p-1.5 rounded-md transition-colors",
                        hasDuplicates && duplicateSearchEnabled
                          ? "text-yellow-500 animate-pulse cursor-pointer hover:bg-yellow-50"
                          : "text-muted-foreground opacity-40 cursor-default"
                      )}
                    >
                      <InfoIcon className="w-5 h-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {!duplicateSearchEnabled
                      ? "Duplicate detection is disabled"
                      : !search
                      ? "Run a search to detect duplicate accounts"
                      : isDuplicatesLoading
                      ? "Checking for duplicates…"
                      : hasDuplicates
                      ? `${duplicateGroups.length} duplicate identifier${duplicateGroups.length !== 1 ? "s" : ""} found — click to view`
                      : "No duplicate accounts in current results"}
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>
        </>
      }
      data={users}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load users"
      emptyState={{ message: "No users found matching your criteria" }}
      contentClassName="p-0 mt-0"
    >
      <UserTable
        users={users}
        onUserClick={(id) => {
          setSelectedUserId(id);
          setIsDrawerOpen(true);
        }}
      />

      {users.length > 0 && (
        <div className="mt-4">
          <CustomPaginationComponent
            currentPage={data?.currentPage ?? 0}
            lastPage={(data?.lastPage ?? 0) + 1}
            onPageChange={(newPage) => {
              const params = new URLSearchParams(searchParams);
              params.set("page", newPage.toString());
              setSearchParams(params);
            }}
          />
        </div>
      )}

      <UserDetailsDrawer
        userId={selectedUserId}
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
      />

      <DuplicateAccountsSheet
        isOpen={isDuplicatesSheetOpen}
        onOpenChange={setIsDuplicatesSheetOpen}
        groups={duplicateGroups}
        isLoading={isDuplicatesLoading}
        onUserClick={(id) => {
          setSelectedUserId(id);
          setIsDrawerOpen(true);
        }}
      />
    </ContentLayout>
  );
};

export default UserList;
